import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Award, Sparkles, PartyPopper, Share2, Printer, ChevronRight, Folder } from "lucide-react";
import ElementaryMissionPage from "./ElementaryMissionPage";
import LemonBotJr from "@/components/LemonBotJr";
import { BUSINESS_TEMPLATES_FOLDER } from "@/data/externalResources";
import {
  loadJrState,
  saveJrState,
  markMissionComplete,
  JR_BIRDS,
  JR_BRAND_COLORS,
} from "@/utils/jrDemoState";
import {
  playClick,
  playCorrect,
  playCelebrate,
} from "@/utils/jrSound";
import { speak, stopSpeaking } from "@/utils/jrVoice";

/**
 * Mission 9 — Graduation Hall.
 *
 * The emotional payoff of the whole journey:
 *   1. Kid types their first name (or it's pre-filled).
 *   2. Animated confetti + voice congrats.
 *   3. Reveals their personal Junior CEO Certificate populated from
 *      everything they did in missions 1-8 (problem, bird, brand,
 *      slogan, money mission rounds, pitch).
 *   4. CTAs: print, share, back to CEO City.
 *
 * Pure frontend. Reads from localStorage. Marks mission 9 complete on reveal.
 */

// --- Confetti -----------------------------------------------------------
const CONFETTI_COLORS = ["#fde047", "#fb923c", "#ef4444", "#22c55e", "#3b82f6", "#a855f7", "#f472b6"];

function Confetti({ active, count = 80 }) {
  const pieces = useMemo(() => {
    if (!active) return [];
    return Array.from({ length: count }).map((_, i) => ({
      id: i,
      left: Math.random() * 100,
      delay: Math.random() * 1.5,
      duration: 2.5 + Math.random() * 2,
      color: CONFETTI_COLORS[i % CONFETTI_COLORS.length],
      rotate: Math.random() * 360,
      size: 6 + Math.random() * 8,
      shape: i % 3, // 0 square, 1 circle, 2 streamer
    }));
  }, [active, count]);

  if (!active) return null;
  return (
    <div className="pointer-events-none fixed inset-0 z-50 overflow-hidden" aria-hidden="true">
      {pieces.map((p) => (
        <span
          key={p.id}
          className="absolute animate-confetti-fall"
          style={{
            left: `${p.left}%`,
            top: "-10%",
            width: p.shape === 2 ? p.size * 0.4 : p.size,
            height: p.shape === 2 ? p.size * 1.6 : p.size,
            backgroundColor: p.color,
            transform: `rotate(${p.rotate}deg)`,
            borderRadius: p.shape === 1 ? "50%" : p.shape === 2 ? "1px" : "2px",
            animationDelay: `${p.delay}s`,
            animationDuration: `${p.duration}s`,
          }}
        />
      ))}
      <style>{`
        @keyframes confetti-fall {
          0%   { transform: translate3d(0, -10vh, 0) rotate(0deg); opacity: 1; }
          80%  { opacity: 1; }
          100% { transform: translate3d(0, 110vh, 0) rotate(720deg); opacity: 0; }
        }
        .animate-confetti-fall {
          animation-name: confetti-fall;
          animation-timing-function: cubic-bezier(0.4, 0.1, 0.6, 1);
          animation-fill-mode: forwards;
        }
      `}</style>
    </div>
  );
}

// --- Mini Brand Mark (matches Mission 5 preview) ------------------------
function BrandMark({ colorKey, shapeKey, size = 80 }) {
  const color = JR_BRAND_COLORS.find((c) => c.key === colorKey) || JR_BRAND_COLORS[0];
  const shapes = { lemon: "🍋", star: "⭐", heart: "❤️", cloud: "☁️" };
  const shape = shapes[shapeKey] || shapes.lemon;
  return (
    <div
      className={`${color.bg} rounded-2xl flex items-center justify-center shadow`}
      style={{ width: size, height: size, boxShadow: `0 6px 18px -4px ${color.hex}aa` }}
      aria-hidden="true"
    >
      <span style={{ fontSize: size * 0.55 }}>{shape}</span>
    </div>
  );
}

// --- Mission 9 ----------------------------------------------------------
export default function Mission9() {
  const navigate = useNavigate();
  const state = loadJrState();
  const [name, setName] = useState((state.studentName || "").trim());
  const [phase, setPhase] = useState(state.studentName ? "reveal" : "name"); // name → reveal
  const [showConfetti, setShowConfetti] = useState(false);
  const [showOverlay, setShowOverlay] = useState(false);

  const bird = JR_BIRDS[state.bizBird] || JR_BIRDS.eagle;
  const today = new Date().toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" });

  const displayName = name.trim() || "Junior CEO";
  const displayBrand = state.brandName || state.businessIdea || "Pencil Power Station";
  const displayProblem = state.problem || state.businessIdea || "Helping students stay ready to learn";
  const displayCustomer = state.customer || "Students";
  const displayHelp = state.helpKind || "Solve a small problem";
  const displaySlogan = state.signSlogan || "Always Ready to Learn!";
  const displayPitch = state.pitch || `My business helps ${displayCustomer.toLowerCase()} by ${displayHelp.toLowerCase()}.`;

  // Cleanup speech on unmount.
  useEffect(() => () => stopSpeaking(), []);

  const handleBeginCeremony = () => {
    if (name.trim().length < 1) return;
    playClick();
    saveJrState({ studentName: name.trim() });
    setPhase("reveal");
    runRevealSequence(name.trim());
  };

  const runRevealSequence = (resolvedName) => {
    // 1. Confetti immediately.
    setShowConfetti(true);
    playCelebrate();

    // 2. Voice congrats (browser SpeechSynthesis; no-op if unavailable).
    speak(
      `Congratulations, ${resolvedName}! You are officially a Junior CEO! Every mission complete. Every idea brave. Every problem solved. Welcome to the Junior CEO Hall of Fame!`,
      { rate: 0.9, pitch: 1.05 }
    );

    // 3. Mark mission 9 complete.
    markMissionComplete(9, { customized: !!resolvedName });

    // 4. Stop confetti after 6s, but keep cert visible.
    setTimeout(() => setShowConfetti(false), 6000);
  };

  const handleReplay = () => {
    playClick();
    setShowConfetti(true);
    playCelebrate();
    speak(`Congratulations, ${displayName}! You are a Junior CEO!`);
    setTimeout(() => setShowConfetti(false), 5000);
  };

  const handleShare = async () => {
    playClick();
    const shareUrl = `${window.location.origin}/share/elementary-preview`;
    const shareText = `🍋 ${displayName} just became a Junior CEO! Check out the Lemonade Leaders Jr. adventure: ${shareUrl}`;
    if (navigator.share) {
      try {
        await navigator.share({ title: "Junior CEO!", text: shareText, url: shareUrl });
        return;
      } catch {
        /* fall through to clipboard */
      }
    }
    try {
      await navigator.clipboard.writeText(shareText);
      alert("Share link copied to clipboard!");
    } catch {
      window.prompt("Copy this share link:", shareUrl);
    }
  };

  const handlePrint = () => {
    playClick();
    window.print();
  };

  // ===== Phase: name input =====
  if (phase === "name") {
    return (
      <ElementaryMissionPage
        missionNumber={9}
        title="Graduation Hall"
        lesson="You finished every mission. Time to get your Junior CEO Certificate!"
        canContinue={name.trim().length >= 1}
        onContinue={handleBeginCeremony}
        continueLabel="Start the Ceremony!"
        testId="elementary-mission-9-page"
      >
        <div className="mb-5">
          <LemonBotJr
            tone="green"
            greeting="You did it! Every mission is done. Type your first name so I can announce you to the Junior CEO Hall of Fame!"
            prompts={[
              "Just your first name is fine.",
              "Press 'Start the Ceremony!' when you're ready.",
              "Get ready for confetti!",
            ]}
            compact
          />
        </div>

        <div className="bg-gradient-to-br from-yellow-100 via-amber-100 to-orange-100 ring-2 ring-yellow-300 rounded-3xl p-6 sm:p-10 text-center shadow-inner">
          <div className="inline-flex items-center justify-center w-20 h-20 bg-yellow-500 text-white rounded-full shadow-lg mb-4">
            <Award className="w-10 h-10" />
          </div>
          <h2 className="text-2xl sm:text-3xl font-black text-gray-900 mb-2">
            Welcome to Graduation Hall
          </h2>
          <p className="text-gray-700 mb-6">What's your first name, future CEO?</p>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && handleBeginCeremony()}
            maxLength={24}
            placeholder="Your first name"
            className="w-full max-w-xs mx-auto px-5 py-4 text-2xl font-bold text-center border-3 border-yellow-300 rounded-2xl focus:ring-4 focus:ring-yellow-400 focus:border-yellow-500 outline-none bg-white"
            data-testid="mission-9-name-input"
            autoFocus
          />
          <p className="mt-3 text-xs text-gray-500">{name.length}/24</p>
        </div>
      </ElementaryMissionPage>
    );
  }

  // ===== Phase: reveal / certificate =====
  return (
    <ElementaryMissionPage
      missionNumber={9}
      title="Graduation Hall"
      lesson="You did it! You finished every Junior CEO mission. Here is your certificate."
      canContinue={true}
      onContinue={() => {
        playClick();
        navigate("/elementary/demo");
      }}
      continueLabel="Back to CEO City"
      testId="elementary-mission-9-page"
    >
      <Confetti active={showConfetti} />

      {/* LemonBot Jr. announcement */}
      <div className="mb-5">
        <LemonBotJr
          tone="green"
          greeting={`Congratulations, ${displayName}! You are officially a Junior CEO!`}
          prompts={[
            "Take a screenshot or print this for your family!",
            "Every problem solved, every idea brave — that's you.",
            "Welcome to the Junior CEO Hall of Fame.",
          ]}
          compact
        />
      </div>

      {/* Big animated reveal card */}
      <div className="relative bg-gradient-to-br from-yellow-50 via-white to-amber-50 ring-4 ring-yellow-400 rounded-[28px] p-6 sm:p-10 shadow-2xl overflow-hidden mb-6 animate-cert-rise">
        {/* Decorative corner glows */}
        <span className="absolute -top-12 -left-12 w-44 h-44 bg-yellow-300/40 rounded-full blur-3xl" aria-hidden="true" />
        <span className="absolute -bottom-12 -right-12 w-52 h-52 bg-orange-300/40 rounded-full blur-3xl" aria-hidden="true" />

        {/* Top eyebrow */}
        <div className="relative text-center">
          <div className="inline-flex items-center gap-2 bg-green-500 text-white px-3 py-1 rounded-full text-xs font-black uppercase tracking-wider shadow mb-3">
            <PartyPopper className="w-3.5 h-3.5" />
            Mission Complete · 9/9
          </div>
          <p className="text-xs sm:text-sm font-bold uppercase tracking-widest text-orange-600 mb-3">
            The Lemonade Stand · Lemonade Leaders Jr.
          </p>

          <div className="flex justify-center mb-3">
            <div className="bg-yellow-500 text-white w-16 h-16 rounded-full flex items-center justify-center shadow-xl ring-4 ring-yellow-200 animate-cert-trophy">
              <Award className="w-9 h-9" />
            </div>
          </div>

          <h2 className="text-3xl sm:text-5xl font-black text-gray-900 mb-2 leading-tight">
            Junior CEO Certificate
          </h2>
          <p className="text-gray-500 text-sm uppercase tracking-wider mb-1">Proudly awarded to</p>
          <p
            className="text-3xl sm:text-5xl font-bold text-yellow-600 mb-6 font-serif italic break-words"
            data-testid="mission-9-cert-name"
          >
            {displayName}
          </p>

          {/* Brand mark + tagline */}
          <div className="flex flex-col items-center gap-3 mb-6">
            <BrandMark colorKey={state.brandColor} shapeKey={state.brandShape} size={72} />
            <div>
              <p className="text-xs font-bold text-yellow-700 uppercase tracking-wide">Founder of</p>
              <p className="text-xl sm:text-2xl font-black text-gray-900" data-testid="mission-9-cert-brand">
                {displayBrand}
              </p>
              <p className="text-sm text-gray-600 italic mt-1">"{displaySlogan}"</p>
            </div>
          </div>

          {/* Achievement summary tiles */}
          <div className="grid sm:grid-cols-3 gap-3 max-w-3xl mx-auto mb-6 text-left">
            <div className="bg-white ring-1 ring-amber-200 rounded-2xl p-4 shadow-sm">
              <p className="text-[10px] font-bold text-amber-700 uppercase tracking-wide mb-2">Biz Bird</p>
              <p className="text-gray-900 font-bold flex items-center gap-2">
                {bird.image ? (
                  <img src={bird.image} alt="" className="w-8 h-8 rounded-full object-contain bg-white ring-1 ring-amber-200" />
                ) : (
                  <span className="text-xl" aria-hidden="true">{bird.emoji}</span>
                )}
                <span>{bird.name}</span>
              </p>
            </div>
            <div className="bg-white ring-1 ring-rose-200 rounded-2xl p-4 shadow-sm">
              <p className="text-[10px] font-bold text-rose-700 uppercase tracking-wide mb-2">Helps</p>
              <p className="text-gray-900 font-bold">{displayCustomer}</p>
              <p className="text-xs text-gray-500 mt-1">by {displayHelp.toLowerCase()}</p>
            </div>
            <div className="bg-white ring-1 ring-emerald-200 rounded-2xl p-4 shadow-sm">
              <p className="text-[10px] font-bold text-emerald-700 uppercase tracking-wide mb-2">Money Mission</p>
              <p className="text-gray-900 font-bold">
                {state.coinGameBestRounds || 0}/3 rounds
              </p>
              <p className="text-xs text-gray-500 mt-1">
                {state.coinGameUsedHelper ? "Used helper" : "No helper used!"}
              </p>
            </div>
          </div>

          {/* Pitch quote */}
          <div className="bg-yellow-100/80 ring-1 ring-yellow-300 rounded-2xl p-4 max-w-3xl mx-auto mb-6 text-left">
            <p className="text-[10px] font-bold text-yellow-800 uppercase tracking-wide mb-1">My Junior CEO Pitch</p>
            <p className="text-gray-900 italic leading-relaxed" data-testid="mission-9-cert-pitch">"{displayPitch}"</p>
          </div>

          {/* Big body */}
          <p className="text-base sm:text-lg text-gray-700 max-w-2xl mx-auto mb-6 leading-relaxed">
            For solving problems, building a brand, counting coins, designing
            signs, pitching ideas, and finishing every Junior CEO mission with
            heart and courage.
          </p>

          {/* Signatures */}
          <div className="grid sm:grid-cols-2 gap-8 pt-4 max-w-2xl mx-auto mb-2">
            <div>
              <div className="border-b-2 border-gray-300 mb-2 h-8 flex items-end justify-center">
                <span className="font-serif italic text-gray-500 text-lg">LemonBot Jr.</span>
              </div>
              <p className="text-xs text-gray-500 uppercase tracking-wide">Junior CEO Coach</p>
            </div>
            <div>
              <div className="border-b-2 border-gray-300 mb-2 h-8 flex items-end justify-center">
                <span className="font-serif italic text-gray-500 text-lg">{today}</span>
              </div>
              <p className="text-xs text-gray-500 uppercase tracking-wide">Date Earned</p>
            </div>
          </div>
        </div>
      </div>

      {/* Action strip */}
      <div className="flex flex-wrap gap-3 justify-center print:hidden">
        <button
          onClick={handleReplay}
          className="inline-flex items-center gap-2 bg-yellow-500 hover:bg-yellow-600 text-white px-5 py-3 rounded-full font-bold transition-colors shadow"
          data-testid="mission-9-replay"
        >
          <Sparkles className="w-4 h-4" /> Play Confetti Again
        </button>
        <button
          onClick={handleShare}
          className="inline-flex items-center gap-2 bg-white ring-2 ring-yellow-300 hover:ring-yellow-500 text-gray-800 px-5 py-3 rounded-full font-bold transition-colors"
          data-testid="mission-9-share"
        >
          <Share2 className="w-4 h-4" /> Share My Win
        </button>
        <button
          onClick={handlePrint}
          className="inline-flex items-center gap-2 bg-white ring-2 ring-gray-200 hover:ring-gray-400 text-gray-800 px-5 py-3 rounded-full font-bold transition-colors"
          data-testid="mission-9-print"
        >
          <Printer className="w-4 h-4" /> Print Certificate
        </button>
        <button
          onClick={() => {
            playClick();
            const birdParam = state.bizBird || "eagle";
            const businessParam = encodeURIComponent(displayBrand);
            const nameParam = encodeURIComponent(displayName);
            navigate(`/elementary/demo/certificate-preview?name=${nameParam}&business=${businessParam}&bird=${birdParam}`);
          }}
          className="inline-flex items-center gap-2 bg-orange-500 hover:bg-orange-600 text-white px-5 py-3 rounded-full font-bold transition-colors"
          data-testid="mission-9-fullscreen"
        >
          Fullscreen Certificate <ChevronRight className="w-4 h-4" />
        </button>
      </div>

      {/* Take-home pack — for parents/educators */}
      <div className="mt-8 max-w-3xl mx-auto print:hidden">
        <div className="bg-white ring-1 ring-yellow-200 rounded-3xl p-5 sm:p-6 shadow-sm flex flex-col sm:flex-row items-start sm:items-center gap-4">
          <span className="bg-yellow-100 text-yellow-700 w-12 h-12 rounded-2xl flex items-center justify-center shrink-0">
            <Folder className="w-5 h-5" />
          </span>
          <div className="flex-1">
            <p className="text-sm font-bold text-yellow-700 uppercase tracking-wide mb-1">For grown-ups</p>
            <h3 className="text-base sm:text-lg font-bold text-gray-900">Keep the Junior CEO momentum going</h3>
            <p className="text-sm text-gray-600 mt-1">
              Open our Business Templates folder for printable worksheets,
              budgeting sheets, and pitch templates kids can use at home.
            </p>
          </div>
          <a
            href={BUSINESS_TEMPLATES_FOLDER}
            target="_blank"
            rel="noopener noreferrer"
            className="bg-yellow-500 hover:bg-yellow-600 text-white px-4 py-2.5 rounded-full font-bold text-sm shadow shrink-0 inline-flex items-center gap-2"
            data-testid="mission-9-take-home-pack"
          >
            <Folder className="w-4 h-4" /> Open Take-Home Pack
          </a>
        </div>
      </div>

      {/* Inline animations */}
      <style>{`
        @keyframes cert-rise {
          0%   { opacity: 0; transform: translateY(40px) scale(0.95); }
          60%  { opacity: 1; transform: translateY(-8px) scale(1.01); }
          100% { opacity: 1; transform: translateY(0) scale(1); }
        }
        .animate-cert-rise { animation: cert-rise 0.9s cubic-bezier(0.22, 1, 0.36, 1) both; }

        @keyframes cert-trophy {
          0%, 100% { transform: rotate(0deg) scale(1); }
          25%      { transform: rotate(-8deg) scale(1.05); }
          75%      { transform: rotate(8deg) scale(1.05); }
        }
        .animate-cert-trophy { animation: cert-trophy 1.6s ease-in-out infinite; }
      `}</style>
    </ElementaryMissionPage>
  );
}
